import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Signal,
  computed,
  inject,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { IntercomTargetDirective } from '@principle-theorem/ng-intercom';
import {
  BasicDialogService,
  CollapsibleComponent,
  CollapsibleContentDirective,
  DialogPresets,
  IActionButton,
  MapPipe,
  MomentPipe,
  ProfileImagePipe,
  ProfileImageService,
  SplitCamelPipe,
  TrackByFunctions,
  TruncateTooltipDirective,
  UserIconComponent,
  toSignal,
} from '@principle-theorem/ng-shared';
import {
  ITypesenseTaskWithRef,
  LabJob,
  MarketingCampaignEntry,
  Patient,
  Recalls,
  Task,
} from '@principle-theorem/principle-core';
import { MARKETING_CAMPAIGNS_FEATURE } from '@principle-theorem/principle-core/features';
import {
  IAppointment,
  IBrand,
  ILabJob,
  IPatientFormSchema,
  IPatientRecall,
  PatientMetadataDisplayTarget,
  PatientStatus,
  RecallTrack,
  type IPatient,
  type ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_WITH_YEAR,
  DAY_MONTH_YEAR_FORMAT,
  DocumentReference,
  Firestore,
  filterUndefined,
  multiFilter,
  multiFind,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { MomentModule } from 'ngx-moment';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  of,
  type Observable,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CurrentBrandScope } from '../../app-state/current-brand';
import { DateService } from '../../date.service';
import { AccountSummaryBloc } from '../../models/account-summary-bloc';
import { BrandRouterLinkDirective } from '../../navigation/brand-router-link.directive';
import { StateBasedNavigationService } from '../../navigation/state-based-navigation.service';
import { TimelineNavigationService } from '../../navigation/timeline-navigation-service';
import { TypesenseSearchService } from '../../typesense/typesense-search.service';
import {
  IPatientMarketingCampaignsDialogRequest,
  PatientMarketingCampaignsDialogComponent,
} from '../marketing-campaigns/patient-marketing-campaigns-dialog/patient-marketing-campaigns-dialog.component';
import { NewPatientIconComponent } from '../new-patient-icon/new-patient-icon.component';
import { PatientDepositRequiredBadgeComponent } from '../patient-deposit-required-badge/patient-deposit-required-badge.component';
import { PatientMetadataDisplayOutletComponent } from '../patient-metadata/patient-metadata-display-outlet/patient-metadata-display-outlet.component';
import { PatientStatusChipComponent } from '../patient-status-chip/patient-status-chip.component';
import { TagDisplayListComponent } from '../tag-display-list/tag-display-list.component';

export type PatientProfileSummaryWorkflowSection = 'tasks' | 'labJobs';

/**
 * Optional DI contract. Hosts able to open the workflow dialog provide an
 * implementation; hosts that don't won't render the workflow links.
 */
export abstract class PatientProfileSummaryWorkflowDialogService {
  abstract open(
    patient: WithRef<IPatient>,
    section: PatientProfileSummaryWorkflowSection
  ): void;
}

@Component({
  selector: 'pr-patient-profile-summary',
  templateUrl: './patient-profile-summary.component.html',
  styleUrls: ['./patient-profile-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MomentModule,
    MomentPipe,
    MapPipe,
    SplitCamelPipe,
    ProfileImagePipe,
    MatIconButton,
    MatIcon,
    MatMenuModule,
    MatProgressSpinner,
    MatTooltipModule,
    NgTemplateOutlet,
    RouterLink,
    TruncateTooltipDirective,
    NewPatientIconComponent,
    PatientStatusChipComponent,
    PatientMetadataDisplayOutletComponent,
    UserIconComponent,
    TagDisplayListComponent,
    CollapsibleComponent,
    CollapsibleContentDirective,
    BrandRouterLinkDirective,
    MatBadgeModule,
    IntercomTargetDirective,
    PatientDepositRequiredBadgeComponent,
  ],
})
export class PatientProfileSummaryComponent {
  private _dialog = inject(BasicDialogService);
  private _nextRecallAppointments!: Signal<
    Record<RecallTrack, WithRef<IAppointment> | undefined>
  >;
  private _brandScope = inject(CurrentBrandScope);
  private _search = inject(TypesenseSearchService);
  private _workflowDialog = inject(PatientProfileSummaryWorkflowDialogService, {
    optional: true,
  });
  readonly hasWorkflowDialogProvider = !!this._workflowDialog;
  timelineNav = inject(TimelineNavigationService);
  trackByAction = TrackByFunctions.field<IActionButton>('name');
  dateFormat = DAY_MONTH_YEAR_FORMAT;
  profileImage = inject(ProfileImageService);
  patient$ = new ReplaySubject<WithRef<IPatient>>(1);
  expanded$ = new BehaviorSubject<boolean>(true);
  bloc: PatientProfileBloc;
  actions$ = new ReplaySubject<IActionButton[]>(1);
  emailAction$: Observable<IActionButton | undefined>;
  phoneActions$: Observable<IActionButton[]>;
  metadataTargetPatientName =
    PatientMetadataDisplayTarget.AppointmentSidebarBesidePatientName;
  metadataTargetProfileCard =
    PatientMetadataDisplayTarget.AppointmentSidebarUnderTags;
  readonly RecallTrack = RecallTrack;
  readonly patientStatus = PatientStatus;
  readonly brand: Signal<WithRef<IBrand> | undefined>;
  readonly enabledTracks = computed(() =>
    this.brand()?.settings.secondaryRecalls
      ? [RecallTrack.Primary, RecallTrack.Secondary]
      : [RecallTrack.Primary]
  );
  marketingFeature = MARKETING_CAMPAIGNS_FEATURE;
  activeCampaignEntryCount: Signal<number>;
  activeTasks: Signal<ITypesenseTaskWithRef[]>;
  activeLabJobs: Signal<WithRef<ILabJob>[]>;
  activeTaskCount: Signal<number>;
  overdueTaskCount: Signal<number>;
  activeLabJobCount: Signal<number>;
  overdueLabJobCount: Signal<number>;
  hasOverdueActiveWorkflowItem: Signal<boolean>;

  @Input()
  set actions(actions: IActionButton[]) {
    if (actions) {
      this.actions$.next(actions);
    }
  }

  @Input({ transform: coerceBooleanProperty }) expandable = false;

  @Input()
  set patient(patient: WithRef<IPatient> | undefined) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  constructor(
    public stateNav: StateBasedNavigationService,
    public dateService: DateService
  ) {
    this.bloc = new PatientProfileBloc(this.patient$);
    this.emailAction$ = this.actions$.pipe(
      multiFind((action) => action.name === 'Email')
    );
    this.phoneActions$ = this.actions$.pipe(
      multiFilter((action) => ['Call', 'SMS'].includes(action.name))
    );
    this._nextRecallAppointments = toSignal(
      this.patient$.pipe(
        switchMap(async (patient) => {
          const [primary, secondary] = await Promise.all([
            this._fetchNextRecallAppointment(
              patient.recall?.nextRecallAppointment
            ),
            this._fetchNextRecallAppointment(
              patient.secondaryRecall?.nextRecallAppointment
            ),
          ]);
          return {
            [RecallTrack.Primary]: primary,
            [RecallTrack.Secondary]: secondary,
          };
        })
      ),
      {
        [RecallTrack.Primary]: undefined,
        [RecallTrack.Secondary]: undefined,
      }
    );

    const brand$ = this.patient$.pipe(
      switchMap((patient) => Firestore.getDoc(Patient.brandRef(patient)))
    );
    this.brand = toSignal(brand$);

    const activeCampaignEntryCount$ = this.patient$.pipe(
      switchMap((patient) => Patient.getCampaignEntries$(patient.ref)),
      multiFilter((entry) => MarketingCampaignEntry.isActive(entry)),
      map((entries) => entries.length)
    );
    this.activeCampaignEntryCount = toSignal(activeCampaignEntryCount$, 0);

    this.activeTasks = toSignal(
      this.patient$.pipe(
        switchMap((patient) => this._search.patientTaskQuery$(patient.ref)),
        map((results) => results.results)
      ),
      []
    );

    this.activeLabJobs = toSignal(
      combineLatest([
        this.patient$,
        this._brandScope.doc$.pipe(filterUndefined()),
      ]).pipe(
        switchMap(([patient, brand]) => Patient.labJobs$(patient, brand)),
        multiFilter((labJob) => !LabJob.complete(labJob))
      ),
      []
    );

    this.activeTaskCount = computed(() => this.activeTasks().length);
    this.overdueTaskCount = computed(
      () => this.activeTasks().filter((task) => Task.late(task)).length
    );
    this.activeLabJobCount = computed(() => this.activeLabJobs().length);
    this.overdueLabJobCount = computed(
      () =>
        this.activeLabJobs().filter((labJob) => LabJob.isOverdue(labJob)).length
    );
    this.hasOverdueActiveWorkflowItem = computed(
      () => !!this.overdueTaskCount() || !!this.overdueLabJobCount()
    );
  }

  isDisabledPatient(status: PatientStatus): boolean {
    return Patient.isDisabledPatient(status);
  }

  nextRecallAppointment(track: RecallTrack): WithRef<IAppointment> | undefined {
    return this._nextRecallAppointments()[track];
  }

  getRecall(
    patient: WithRef<IPatient>,
    track: RecallTrack
  ): IPatientRecall | undefined {
    return patient[Recalls.trackToField(track)];
  }

  trackLabel(track: RecallTrack): string {
    return Recalls.trackLabel(this.brand(), track);
  }

  async openPatientCampaigns(): Promise<void> {
    const patient = await snapshot(this.patient$);
    await this._dialog
      .open<
        PatientMarketingCampaignsDialogComponent,
        IPatientMarketingCampaignsDialogRequest,
        void
      >(
        PatientMarketingCampaignsDialogComponent,
        DialogPresets.large({ data: { patient } })
      )
      .afterClosed()
      .toPromise();
  }

  async openActiveWorkflowItems(
    section: PatientProfileSummaryWorkflowSection
  ): Promise<void> {
    if (!this._workflowDialog) {
      return;
    }
    const patient = await snapshot(this.patient$);
    this._workflowDialog.open(patient, section);
  }

  private async _fetchNextRecallAppointment(
    ref?: DocumentReference<IAppointment>
  ): Promise<WithRef<IAppointment> | undefined> {
    if (!ref) {
      return;
    }
    const appointment = await Firestore.getDoc(ref);

    if (!appointment?.event?.from) {
      return;
    }
    return appointment;
  }
}

export class PatientProfileBloc {
  readonly dobFormat = DATE_WITH_YEAR;
  latestMedicalForm$: Observable<IPatientFormSchema | undefined>;
  tags$: Observable<WithRef<ITag>[]>;
  age$: Observable<string | undefined>;
  phoneNumber$: Observable<string | undefined>;
  phoneLabel$: Observable<string | undefined>;
  healthFundName$: Observable<string | undefined>;
  account: AccountSummaryBloc;

  constructor(private _patient$: Observable<WithRef<IPatient>>) {
    this.account = new AccountSummaryBloc(
      this._patient$.pipe(filterUndefined())
    );
    this.tags$ = this._patient$.pipe(
      switchMap((patient) => (patient ? Patient.tags$(patient) : of([])))
    );
    this.age$ = this._patient$.pipe(
      map((patient) => (patient ? Patient.displayAge(patient) : undefined))
    );

    const contactNumber$ = this._patient$.pipe(
      map((patient) => (patient ? first(patient.contactNumbers) : undefined))
    );
    this.phoneNumber$ = contactNumber$.pipe(
      map((contactNumber) => contactNumber?.number)
    );
    this.phoneLabel$ = contactNumber$.pipe(
      map((contactNumber) => contactNumber?.label)
    );
    this.healthFundName$ = this._patient$.pipe(
      map((patient) => patient.healthFundCard?.fundCode)
    );
  }
}
